import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, AlertCircle, ArrowLeft, UserPlus } from '../../components/icons';
import { supabase } from '../../lib/supabase';
import { verifyAssessmentCode } from '../../lib/assessment-codes';
import { checkUserRole } from '../../lib/auth';
import { useAuth } from '../../contexts/AuthContext';
import { autoCreateLead } from '../../lib/crm';

export function RegistrationPageTR() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    assessmentCode: '',
    privacyAccepted: false,
    termsAccepted: false,
    disclaimerAccepted: false,
    gdprAccepted: false,
    marketingAccepted: false
  });

  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        const role = await checkUserRole(user.id);
        switch (role) {
          case 'admin':
            navigate('/admin');
            break;

          case 'partner':
            navigate('/tr/partner');
            break;
          case 'coach':
            navigate('/tr/coach');
            break;
          case 'respondent':
            navigate('/tr/dashboard');
            break;
        }
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    })();
  }, [user, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { isValid, coachId, teamId, autoAiAnalysis } = await verifyAssessmentCode(formData.assessmentCode.trim());
      if (!isValid) {
        throw new Error('Gecersiz degerlendirme kodu. Lutfen kontrol edip tekrar deneyin.');
      }

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            role: 'respondent',
          },
        },
      });

      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error('Kayit basarisiz oldu');

      const { error: userError } = await supabase
        .from('users')
        .update({ full_name: formData.fullName })
        .eq('id', authData.user.id);

      if (userError) throw userError;

      const { error: agreementError } = await supabase.from('user_agreements').insert({
        user_id: authData.user.id,
        privacy_accepted: formData.privacyAccepted,
        privacy_accepted_at: new Date().toISOString(),
        terms_accepted: formData.termsAccepted,
        terms_accepted_at: new Date().toISOString(),
        disclaimer_accepted: formData.disclaimerAccepted,
        disclaimer_accepted_at: new Date().toISOString(),
        gdpr_accepted: formData.gdprAccepted,
        gdpr_accepted_at: new Date().toISOString(),
      });

      if (agreementError) throw agreementError;

      const { error: respondentError } = await supabase.from('respondents').insert({
        user_id: authData.user.id,
        coach_id: coachId || null,
        ai_analysis_enabled: autoAiAnalysis || false,
        ai_analysis_visible: autoAiAnalysis || false,
      });

      if (respondentError) throw respondentError;

      if (coachId) {
        const codeValue = formData.assessmentCode.trim();
        if (teamId) {
          await (supabase.rpc as any)('increment_code_use_count', { code_value: codeValue });
          await supabase.from('team_members').insert({
            team_id: teamId,
            user_id: authData.user.id,
          });
        } else {
          await supabase
            .from('assessment_codes')
            .update({ is_used: true, used_by: authData.user.id, used_at: new Date().toISOString() })
            .eq('code', codeValue);
        }
      }

      if (coachId) {
        autoCreateLead(coachId, {
          full_name: formData.fullName,
          email: formData.email,
          source: 'assessment_completion',
          user_id: authData.user.id,
        }).catch(() => {});
      }

      let coachName = '';
      if (coachId) {
        const { data: coachUser } = await supabase
          .from('coaches')
          .select('user_id')
          .eq('id', coachId)
          .maybeSingle();
        if (coachUser) {
          const { data: coachData } = await supabase
            .from('users')
            .select('full_name')
            .eq('id', coachUser.user_id)
            .maybeSingle();
          coachName = coachData?.full_name || '';
        }
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`;
      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          type: 'welcome-respondent-tr',
          recipientEmail: formData.email,
          recipientName: formData.fullName,
          coachName,
        }),
      }).catch(() => {});

      navigate('/tr/instructions');

    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'Kayit sirasinda bir hata olustu');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen freud-bg py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/tr"
          className="inline-flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-8 transition-colors font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Ana Sayfaya Don
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mx-auto mb-4">
            <img src="/human_potential_os_logo_V2_cropped.png" alt="NLP OS" className="h-16 w-auto" />
          </div>
          <h1 className="freud-heading-2xl text-chocolate-900 mb-2">
            Degerlendirme Icin Kayit Olun
          </h1>
          <p className="freud-text-base text-chocolate-500">
            Hesabinizi olusturun ve kendinizi kesfedin!
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="freud-card-elevated p-8"
        >
          {error && (
            <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="assessmentCode" className="freud-label block text-chocolate-800 mb-2">Degerlendirme Kodu</label>
              <input
                type="text"
                id="assessmentCode"
                name="assessmentCode"
                value={formData.assessmentCode}
                onChange={handleChange}
                required
                className="freud-input"
                disabled={isLoading}
                placeholder="Degerlendirme kodunuzu girin"
              />
            </div>

            <div>
              <label htmlFor="fullName" className="freud-label block text-chocolate-800 mb-2">Ad Soyad</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="freud-input"
                disabled={isLoading}
                placeholder="Adinizi ve soyadinizi girin"
              />
            </div>

            <div>
              <label htmlFor="email" className="freud-label block text-chocolate-800 mb-2">E-posta Adresi</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="freud-input"
                disabled={isLoading}
                placeholder="E-posta adresinizi girin"
              />
            </div>

            <div>
              <label htmlFor="password" className="freud-label block text-chocolate-800 mb-2">Sifre</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                className="freud-input"
                disabled={isLoading}
                placeholder="Guvenli bir sifre secin (min. 8 karakter)"
              />
            </div>

            <div className="space-y-4 pt-4 border-t border-cream-300">
              <p className="freud-label text-chocolate-700">Gerekli Onaylar</p>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="privacyAccepted"
                  name="privacyAccepted"
                  checked={formData.privacyAccepted}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-500"
                  disabled={isLoading}
                />
                <label htmlFor="privacyAccepted" className="text-chocolate-600 text-sm leading-relaxed">
                  <Link to="/privacy" className="text-sage-600 hover:text-sage-700 font-medium">Gizlilik Politikasini</Link> ve <Link to="/privacy" className="text-sage-600 hover:text-sage-700 font-medium">KVKK/GDPR</Link> okudum ve kabul ediyorum, kisisel verilerimin nasil isleneceginI anliyorum.
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-500"
                  disabled={isLoading}
                />
                <label htmlFor="termsAccepted" className="text-chocolate-600 text-sm leading-relaxed">
                  <Link to="/terms" className="text-sage-600 hover:text-sage-700 font-medium">Kullanim Kosullarini</Link> kabul ediyorum ve kosullarina bagli olmayi onayliyorum.
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="disclaimerAccepted"
                  name="disclaimerAccepted"
                  checked={formData.disclaimerAccepted}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-500"
                  disabled={isLoading}
                />
                <label htmlFor="disclaimerAccepted" className="text-chocolate-600 text-sm leading-relaxed">
                  <Link to="/disclaimer" className="text-sage-600 hover:text-sage-700 font-medium">Sorumluluk Reddi ve Tazminati</Link> onayliyorum ve degerlendirmenin kapsami ile sinirlamalarini anliyorum.
                </label>
              </div>

              <div className="flex items-start gap-3 bg-cream-100 p-4 rounded-xl">
                <input
                  type="checkbox"
                  id="gdprAccepted"
                  name="gdprAccepted"
                  checked={formData.gdprAccepted}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-500"
                  disabled={isLoading}
                />
                <label htmlFor="gdprAccepted" className="text-chocolate-600 text-sm leading-relaxed">
                  Devam ederek, NLP OS'in yalnizca bilgilendirme ve egitim amaciyla tasarlanmis bir kocluk ve kisisel gelisim araci oldugunu kabul ediyorum. Bu bir psikolojik, tibbi veya teshis testi degildir ve sonuclari herhangi bir psikolojik, tibbi veya saglik durumunu teshis etmez, tedavi etmez veya ongormez. Sonuclarin yanitlarima ve profilin metodolojisine dayali oldugunu ve dogruluk, eksiksizlik veya uygulanabilirlik garanti etmeyebilecegini anliyorum. Sonuclari tamamen kendi takdirime gore kullanmayi ve alinan tum kararlar icin tam sorumluluk ustlenmeyi kabul ediyorum. Bu profili ve sonuclarini kullanmamdan kaynaklanan veya bununla ilgili tum talep, yukumluluk, hasar veya kayiplardan, dogrudan, dolayli veya sonuc olarak meydana gelen hasarlar dahil olmak uzere, NLP OS'in yaraticilerini, baglilarini, bagimsiz uygulayicilirini ve ilgili tum taraflari serbest birakmayi, tazmin etmeyi ve zarar gormesini onlemeyi kabul ediyorum.
                </label>
              </div>

              <div className="flex items-center justify-between pt-6">
                <div className="flex items-center gap-2 text-chocolate-500">
                  <Shield className="w-4 h-4 text-sage-600" />
                  <span className="text-sm">Guvenli Kayit</span>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="freud-btn-primary inline-flex items-center gap-2"
                >
                  <UserPlus className="w-5 h-5" />
                  {isLoading ? 'Hesap Olusturuluyor...' : 'Hesap Olustur'}
                </button>
              </div>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-cream-300 text-center">
            <p className="text-chocolate-500 text-sm">
              Zaten bir hesabiniz var mi?{" "}
              <Link to="/tr/login" className="text-coral-600 hover:text-coral-700 font-medium">
                Giris yapin
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
